import { useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  createWorkbook,
  deleteWorkbook,
  toggleWorkbookArchive,
  toggleWorkbookFavorite
} from './workbook-operations';
import { Project } from './workbook-types';
import { useToast } from '@/hooks/errors/use-toast';
import { pageKeys } from './pages/workbook-query-keys';
import { syncProjectDeleted, syncProjectUpdated } from '@/utils/project-metadata-sync';
import { useUser } from '@/hooks/auth/use-user';

interface UseWorkbookOperationsOptions {
  /** Enable optimistic updates for smoother UI experience */
  optimisticUpdates?: boolean;
}

/**
 * Unified workbook operations hook with surgical cache updates and proper optimistic UI
 */
export function useWorkbookOperations(options: UseWorkbookOperationsOptions = {}) {
  const { optimisticUpdates = true } = options; // Default to true for better UX
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user } = useUser(); // For AI context cache sync
  
  // Surgical cache update helpers
  const updateWorkbookInAllQueries = (workbookId: string, updater: (workbook: Project) => Project) => {
    // Update individual workbook query
    queryClient.setQueryData(pageKeys.detail(workbookId), (oldData: Project | undefined) => {
      return oldData ? updater(oldData) : oldData;
    });
    
    // Update workbook lists queries
    queryClient.setQueriesData({ queryKey: ['workbook-projects'] }, (oldData: any) => {
      if (!oldData) return oldData;
      
      const projects = Array.isArray(oldData) ? oldData : oldData.projects;
      if (!projects) return oldData;
      
      const updatedProjects = projects.map((project: Project) => 
        project.id === workbookId ? updater(project) : project
      );
      
      return Array.isArray(oldData) ? updatedProjects : { ...oldData, projects: updatedProjects };
    });
    
    // Update pages queries  
    queryClient.setQueriesData({ queryKey: pageKeys.all }, (oldData: any) => {
      if (!Array.isArray(oldData)) return oldData;
      
      return oldData.map((project: Project) => 
        project.id === workbookId ? updater(project) : project
      );
    });
    
    // Update unified counts query
    queryClient.setQueryData(['workbooks-with-tags-unified'], (oldData: any) => {
      if (!Array.isArray(oldData)) return oldData;
      
      return oldData.map((workbook: any) => 
        workbook.id === workbookId ? { ...workbook, ...updater(workbook as Project) } : workbook
      );
    });
    
    // CRITICAL: Update project navigation query - this is what the editor uses
    queryClient.setQueryData(['project-navigation', workbookId], (oldData: any) => {
      if (!oldData) return oldData;
      
      // Create a temporary Project object for the updater function
      const tempProject = {
        ...oldData.project,
        isFavorite: oldData.project.is_favorite,
        isArchived: oldData.project.is_archived,
        updatedAt: oldData.project.updated_at
      };
      
      // Apply the update
      const updatedTempProject = updater(tempProject);
      
      // Convert back to database format
      const updatedProject = {
        ...oldData.project,
        is_favorite: updatedTempProject.isFavorite,
        is_archived: updatedTempProject.isArchived,
        updated_at: updatedTempProject.updatedAt || new Date().toISOString()
      };
      
      return {
        ...oldData,
        project: updatedProject
      };
    });
  };

  const toggleFavoriteMutation = useMutation({
    mutationFn: ({ id, isFavorite }: { id: string, isFavorite: boolean }) => {
      console.log('[WorkbookOperations] Toggling favorite for workbook:', id, 'to:', isFavorite);
      return toggleWorkbookFavorite(id, isFavorite);
    },
    onMutate: async ({ id, isFavorite }) => {
      if (!optimisticUpdates) return;
      
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: pageKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: ['workbook-projects'] });
      await queryClient.cancelQueries({ queryKey: pageKeys.all });
      await queryClient.cancelQueries({ queryKey: ['project-navigation', id] });
      
      // Snapshot previous values for rollback
      const previousIndividual = queryClient.getQueryData(pageKeys.detail(id));
      const previousLists = queryClient.getQueriesData({ queryKey: ['workbook-projects'] });
      const previousPages = queryClient.getQueriesData({ queryKey: pageKeys.all });
      const previousNavigation = queryClient.getQueryData(['project-navigation', id]);
      
      // Optimistically update all caches
      updateWorkbookInAllQueries(id, (workbook) => ({
        ...workbook,
        isFavorite,
        updatedAt: new Date().toISOString()
      }));
      
      return { previousIndividual, previousLists, previousPages, previousNavigation };
    },
    onError: (error: any, variables, context) => {
      // Rollback optimistic updates
      if (optimisticUpdates && context) {
        if (context.previousIndividual) {
          queryClient.setQueryData(pageKeys.detail(variables.id), context.previousIndividual);
        }
        context.previousLists?.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
        context.previousPages?.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
        if (context.previousNavigation) {
          queryClient.setQueryData(['project-navigation', variables.id], context.previousNavigation);
        }
      }
      
      console.error('[WorkbookOperations] Error toggling favorite:', error);
      const errorMessage = error?.message === 'Cannot favorite an archived workbook' 
        ? 'Archived workbooks cannot be favorited'
        : 'Failed to update favorite status';
      toast({
        title: "Error", 
        description: errorMessage,
        variant: "destructive",
      });
    },
    onSuccess: (updatedWorkbook, variables) => {
      console.log('[WorkbookOperations] Successfully toggled favorite:', updatedWorkbook);
      
      // Surgical update with server response
      updateWorkbookInAllQueries(variables.id, () => updatedWorkbook);
      
      toast({
        title: "Success",
        description: updatedWorkbook.isFavorite ? "Workbook favorited successfully" : "Workbook unfavorited",
      });
    },
  });
  
  const toggleArchiveMutation = useMutation({
    mutationFn: ({ id, isArchived }: { id: string, isArchived: boolean }) => {
      console.log('[WorkbookOperations] Toggling archive for workbook:', id, 'to:', isArchived);
      return toggleWorkbookArchive(id, isArchived);
    },
    onMutate: async ({ id, isArchived }) => {
      if (!optimisticUpdates) return;
      
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: pageKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: ['workbook-projects'] });
      await queryClient.cancelQueries({ queryKey: pageKeys.all });
      await queryClient.cancelQueries({ queryKey: ['project-navigation', id] });
      
      // Snapshot previous values for rollback
      const previousIndividual = queryClient.getQueryData(pageKeys.detail(id));
      const previousLists = queryClient.getQueriesData({ queryKey: ['workbook-projects'] });
      const previousPages = queryClient.getQueriesData({ queryKey: pageKeys.all });
      const previousNavigation = queryClient.getQueryData(['project-navigation', id]);
      
      // Optimistically update all caches
      updateWorkbookInAllQueries(id, (workbook) => ({
        ...workbook,
        isArchived,
        isFavorite: isArchived ? false : workbook.isFavorite, // Auto-unfavorite when archiving
        updatedAt: new Date().toISOString()
      }));
      
      return { previousIndividual, previousLists, previousPages, previousNavigation };
    },
    onError: (error: any, variables, context) => {
      // Rollback optimistic updates
      if (optimisticUpdates && context) {
        if (context.previousIndividual) {
          queryClient.setQueryData(pageKeys.detail(variables.id), context.previousIndividual);
        }
        context.previousLists?.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
        context.previousPages?.forEach(([queryKey, data]: [any, any]) => {
          queryClient.setQueryData(queryKey, data);
        });
        if (context.previousNavigation) {
          queryClient.setQueryData(['project-navigation', variables.id], context.previousNavigation);
        }
      }
      
      console.error('[WorkbookOperations] Error toggling archive:', error);
      toast({
        title: "Error", 
        description: "Failed to update archive status",
        variant: "destructive",
      });
    },
    onSuccess: (updatedWorkbook, variables) => {
      console.log('[WorkbookOperations] Successfully toggled archive:', updatedWorkbook);
      
      // Surgical update with server response
      updateWorkbookInAllQueries(variables.id, () => updatedWorkbook);
      
      const message = updatedWorkbook.isArchived 
        ? "Workbook archived successfully" 
        : "Workbook unarchived";
        
      toast({
        title: "Success",
        description: message,
      });
    },
  });
  
  const deleteWorkbookMutation = useMutation({
    mutationFn: (id: string) => {
      console.log('[WorkbookOperations] Deleting workbook:', id);
      return deleteWorkbook(id);
    },
    onMutate: async (id: string) => {
      if (!optimisticUpdates) return { id };
      
      // Cancel outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: pageKeys.detail(id) });
      await queryClient.cancelQueries({ queryKey: ['workbook-projects'] });
      await queryClient.cancelQueries({ queryKey: pageKeys.all });
      await queryClient.cancelQueries({ queryKey: ['project-navigation', id] });
      await queryClient.cancelQueries({ queryKey: ['workbook-goal', id] });
      
      // Remove from all caches optimistically
      queryClient.removeQueries({ queryKey: pageKeys.detail(id) });
      queryClient.removeQueries({ queryKey: ['project-navigation', id] });
      queryClient.removeQueries({ queryKey: ['workbook-goal', id] });
      
      queryClient.setQueriesData({ queryKey: ['workbook-projects'] }, (oldData: any) => {
        if (!oldData) return oldData;
        
        const projects = Array.isArray(oldData) ? oldData : oldData.projects;
        if (!projects) return oldData;
        
        const filteredProjects = projects.filter((project: Project) => project.id !== id);
        return Array.isArray(oldData) ? filteredProjects : { ...oldData, projects: filteredProjects };
      });
      
      queryClient.setQueriesData({ queryKey: pageKeys.all }, (oldData: any) => {
        if (!Array.isArray(oldData)) return oldData;
        return oldData.filter((project: Project) => project.id !== id);
      });
      
      return { id };
    },
    onError: (error: any, id: string, context) => {
      console.error('[WorkbookOperations] Error deleting workbook:', error);
      
      // Invalidate to restore data on error, but be more specific
      queryClient.invalidateQueries({ queryKey: ['workbook-projects'], exact: false });
      queryClient.invalidateQueries({ queryKey: pageKeys.all });
      
      // Only invalidate project-specific queries if we have context
      if (context?.id) {
        queryClient.invalidateQueries({ queryKey: ['project-navigation', context.id] });
        queryClient.invalidateQueries({ queryKey: ['workbook-goal', context.id] });
      }
      
      toast({
        title: "Error", 
        description: "Failed to delete workbook",
        variant: "destructive",
      });
    },
    onSuccess: (_, deletedId) => {
      console.log('[WorkbookOperations] Successfully deleted workbook:', deletedId);
      
      // Clean up any remaining queries for this project
      queryClient.removeQueries({ queryKey: ['project-navigation', deletedId] });
      queryClient.removeQueries({ queryKey: ['workbook-goal', deletedId] });
      queryClient.removeQueries({ queryKey: pageKeys.detail(deletedId) });
      
      // 🤖 AI CONTEXT: Remove from navigation cache
      if (user?.id) {
        syncProjectDeleted(deletedId, user.id);
      }
      
      toast({
        title: "Success",
        description: "Workbook deleted permanently",
      });
    },
  });
  
  const createWorkbookMutation = useMutation({
    mutationFn: (workbook: Partial<Project>) => {
      console.log('[WorkbookOperations] Creating workbook:', workbook);
      return createWorkbook(workbook);
    },
    onSuccess: (newWorkbook) => {
      console.log('[WorkbookOperations] Successfully created workbook');
      
      // CRITICAL FIX: Invalidate ALL workbook-projects queries regardless of filter parameters
      // This ensures Add to Project dropdowns update immediately when new projects are created
      queryClient.invalidateQueries({ 
        queryKey: ['workbook-projects'], 
        exact: false // IMPORTANT: Match all variations with different filters
      });
      
      // Also invalidate unified counts query for sidebar updates
      queryClient.invalidateQueries({ 
        queryKey: ['workbooks-with-tags-unified'], 
        exact: false 
      });
      
      // BACKUP: Also do optimistic cache updates for immediate UI responsiveness
      // This provides instant feedback while the invalidation refetches data
      queryClient.setQueriesData({ queryKey: ['workbook-projects'] }, (oldData: any) => {
        if (!oldData) return { projects: [newWorkbook], totalCount: 1 };
        
        const projects = Array.isArray(oldData) ? oldData : oldData.projects || [];
        const updatedProjects = [newWorkbook, ...projects];
        
        return Array.isArray(oldData) 
          ? updatedProjects 
          : { ...oldData, projects: updatedProjects, totalCount: (oldData.totalCount || 0) + 1 };
      });
      
      queryClient.setQueriesData({ queryKey: pageKeys.all }, (oldData: any) => {
        if (!Array.isArray(oldData)) return [newWorkbook];
        return [newWorkbook, ...oldData];
      });
      
      // 🤖 AI CONTEXT: Removed immediate sync - handled by database cache refresh system
      // The database cache will populate this within 30 seconds automatically
      // Manual refresh triggers on AI chat opening provide immediate context when needed
    },
    onError: (error: any) => {
      console.error('[WorkbookOperations] Error creating workbook:', error);
      // Re-throw to be caught by the component's try-catch block
      throw error;
    }
  });
  
  return {
    createWorkbook: createWorkbookMutation.mutateAsync,
    toggleFavorite: toggleFavoriteMutation.mutate,
    toggleArchive: toggleArchiveMutation.mutate,
    deleteWorkbook: deleteWorkbookMutation.mutate,
    isPending: 
      createWorkbookMutation.isPending ||
      toggleFavoriteMutation.isPending || 
      toggleArchiveMutation.isPending || 
      deleteWorkbookMutation.isPending,
    // Individual mutation states for button feedback
    isFavoriteToggling: toggleFavoriteMutation.isPending,
    isArchiveToggling: toggleArchiveMutation.isPending,
    isDeleting: deleteWorkbookMutation.isPending,
    isCreating: createWorkbookMutation.isPending
  };
}
